/*
  HALO Galeria: galeria de mídia da seção de compra (bloco HALO Galeria).

  - A rolagem é nativa (dedo, trackpad, teclado), com scroll-snap.
  - As bolinhas mostram a posição; a ativa vira uma barra que enche no tempo de cada imagem
    e, quando termina, a galeria avança (animação CSS, sem temporizador em JavaScript).
  - Segue sozinha mesmo com o mouse em cima ou depois das setas, bolinhas e arraste (a barra
    recomeça na imagem escolhida). Para o cliente, só o botão de pausa para; ele mesmo retoma.
    Sem o cliente perceber, espera fora da tela, com a aba escondida, com o pop-up aberto e
    enquanto o dedo está na imagem. Quem pede menos movimento no aparelho não tem avanço
    automático.
  - Cada mídia sabe a que cor pertence (data-cores, calculado no Liquid). Quando o cliente
    troca a cor no seletor do produto, a galeria mostra só as mídias daquela cor.
  - Vídeos tocam em loop, sem som, só enquanto estão na tela e ativos.
  - Bolinhas e botão "Imagens Real-Life" ficam onde o editor mandar; se encostarem um no
    outro, o botão passa para a borda oposta (.is-flipped).
  - "Imagens Real-Life": abre um <dialog> com as fotos numa faixa contínua (como o
    "Na Mídia") que o cliente arrasta com o dedo ou o mouse; só o botão de pausa para.
*/
(function () {
  'use strict';

  if (!window.customElements || customElements.get('halo-galeria')) return;

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 767px)');

  class HaloGaleria extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.track = this.querySelector('[data-hg-track]');
      if (!this.track) return;
      this.started = true;

      this.ac = new AbortController();
      var on = { signal: this.ac.signal };
      var passive = { signal: this.ac.signal, passive: true };
      var self = this;

      this.reduce = reduceQuery.matches;
      this.frame = this.querySelector('[data-hg-frame]');
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-hg-slide]'));
      this.dotsWrap = this.querySelector('[data-hg-dots]');
      this.nav = this.querySelector('[data-hg-nav]');
      this.prevBtn = this.querySelector('[data-hg-prev]');
      this.nextBtn = this.querySelector('[data-hg-next]');
      this.toggleBtn = this.querySelector('[data-hg-toggle]');
      this.status = this.querySelector('[data-hg-status]');
      this.dialog = this.querySelector('[data-hg-dialog]');
      this.opener = this.querySelector('[data-hg-open]');

      this.option = this.dataset.option || '';
      try {
        this.values = JSON.parse(this.dataset.values || '[]');
      } catch (e) {
        this.values = [];
      }
      this.color = parseInt(this.dataset.color, 10);
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduce;
      this.stopped = !this.autoplay;
      this.reasons = new Set();
      this.index = 0;
      this.inView = false;
      this.programmatic = false;

      if (this.toggleBtn) this.toggleBtn.hidden = !this.autoplay;
      if (this.reduce) {
        this.slides.forEach(function (slide) {
          var video = slide.querySelector('video');
          if (video) video.controls = true;
        });
      }

      this.collect();
      this.setActive(0, false);
      this.updateFlush();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.updateSpots.bind(this));

      // Setas, bolinhas e pausa
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          self.goTo(self.index - 1, true);
        }, on);
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          self.goTo(self.index + 1, true);
        }, on);
      }
      if (this.dotsWrap) {
        this.dotsWrap.addEventListener('click', function (e) {
          var dot = e.target.closest('.halo-galeria__dot');
          if (dot) self.goTo(parseInt(dot.dataset.index, 10), true);
        }, on);
        this.dotsWrap.addEventListener('animationend', function (e) {
          if (e.animationName !== 'halo-galeria-progress') return;
          if (!self.classList.contains('is-playing')) return;
          self.goTo(self.index + 1, false);
        }, on);
      }
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', function () {
          if (self.stopped) self.resume();
          else self.stop();
        }, on);
      }

      // Rolagem nativa: a posição só é lida quando a rolagem assenta.
      this.track.addEventListener('scroll', function () {
        clearTimeout(self.scrollTimer);
        self.scrollTimer = setTimeout(self.settle.bind(self), 120);
      }, passive);
      // Enquanto o dedo está na imagem, o avanço espera (não briga com o arraste).
      this.track.addEventListener('touchstart', function () {
        self.setReason('touch', true);
      }, passive);
      ['touchend', 'touchcancel'].forEach(function (type) {
        self.track.addEventListener(type, function () {
          self.setReason('touch', false);
        }, passive);
      });
      this.track.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        self.goTo(self.index + (e.key === 'ArrowLeft' ? -1 : 1), true);
      }, on);

      // Esperas que o cliente não vê (aba escondida, fora da tela, pop-up aberto).
      document.addEventListener('visibilitychange', function () {
        self.setReason('hidden', document.hidden);
        self.syncVideos();
        if (self.dialog) self.syncReal();
      }, on);

      this.io = new IntersectionObserver(function (entries) {
        self.inView = entries[entries.length - 1].isIntersecting;
        self.setReason('offscreen', !self.inView);
        self.syncVideos();
      }, { threshold: 0.25 });
      this.io.observe(this.frame);

      // Cabeçalho fixo do tema: a galeria fixa logo abaixo dele.
      this.headerOffset = -1;
      this.updateHeader();
      window.addEventListener('scroll', function () {
        clearTimeout(self.headerTimer);
        self.headerTimer = setTimeout(self.updateHeader.bind(self), 100);
      }, passive);

      this.ro = new ResizeObserver(function () {
        self.updateHeader();
        self.updateAlignment();
        self.updateFlush();
        self.updateSpots();
        self.snapTo(self.index);
        if (self.dialog && self.dialog.open) self.measureReal();
      });
      this.ro.observe(this.frame);

      // Troca de cor no seletor do produto
      this.scope = this.closest('xo-product') || document;
      if (this.hasAttribute('data-align-buybox')) {
        this.alignInfo = this.scope.querySelector('.xo-product-info');
        if (this.alignInfo) this.ro.observe(this.alignInfo);
        this.updateAlignment();
      }
      this.scope.addEventListener('change', this.onOptionChange.bind(this), on);

      // Pop-up "Imagens Real-Life"
      if (this.dialog) this.setupReal(on);

      // Editor de temas
      document.addEventListener('shopify:block:select', function (e) {
        var target = e.target;
        if (self.dialog && self.dialog.contains(target)) {
          self.openReal();
          self.stopReal();
          self.scrollToPhoto(target);
          return;
        }
        if (self.dialog && self.dialog.open) self.dialog.close();
        if (target.contains(self)) self.setReason('editor', true);
      }, on);
      document.addEventListener('shopify:block:deselect', function (e) {
        if (e.target.contains(self)) self.setReason('editor', false);
      }, on);
    }

    disconnectedCallback() {
      if (!this.started) return;
      this.started = false;
      this.ac.abort();
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      clearTimeout(this.scrollTimer);
      clearTimeout(this.headerTimer);
      if (this.realRaf) cancelAnimationFrame(this.realRaf);
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    // Mídias visíveis (da cor atual) e bolinhas
    collect() {
      this.visible = this.slides.filter(function (slide) {
        return !slide.hidden;
      });
      var count = this.visible.length;
      this.dataset.count = String(count);
      this.classList.toggle('is-playing', !this.stopped && count > 1);

      if (!this.dotsWrap) return;
      this.dotsWrap.textContent = '';
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('span');
        dot.className = 'halo-galeria__dot';
        dot.dataset.index = String(i);
        dot.innerHTML = '<span class="halo-galeria__dot-bar"><span class="halo-galeria__dot-fill"></span></span>';
        this.dotsWrap.appendChild(dot);
      }
      this.updateSpots();
    }

    // Bolinhas e botão "Imagens Real-Life" encostados: o botão vai para a borda oposta.
    updateSpots() {
      if (!this.opener || !this.nav) return;
      this.classList.remove('is-flipped');
      if (this.visible.length < 2) return;
      var a = this.opener.getBoundingClientRect();
      var b = this.nav.getBoundingClientRect();
      var gap = 8;
      var touching = a.left < b.right + gap && b.left < a.right + gap && a.top < b.bottom + gap && b.top < a.bottom + gap;
      this.classList.toggle('is-flipped', touching);
    }

    setActive(i, announce) {
      this.index = i;
      // Sem botão de pausa no celular: a troca feita pelo cliente (deslizar, setas, bolinhas)
      // para o avanço sozinho, para ninguém ficar sem como parar o movimento.
      if (announce && this.hasAttribute('data-no-pause-mobile') && mobileQuery.matches) this.stop();
      if (this.dotsWrap) {
        var dots = this.dotsWrap.children;
        for (var k = 0; k < dots.length; k++) dots[k].classList.toggle('is-active', k === i);
      }
      if (announce && this.status) {
        this.status.textContent = (this.dataset.labelStatus || '')
          .replace('[n]', String(i + 1))
          .replace('[total]', String(this.visible.length));
      }
      this.syncVideos();
    }

    goTo(i, byUser) {
      var count = this.visible.length;
      if (count < 2) return;
      i = ((i % count) + count) % count;
      var left = i * this.track.clientWidth;
      this.programmatic = Math.abs(this.track.scrollLeft - left) > 1;
      this.track.scrollTo({ left: left, behavior: this.reduce ? 'auto' : 'smooth' });
      this.setActive(i, byUser);
    }

    // Reposiciona sem animação (troca de cor, mudança de largura).
    snapTo(i) {
      var left = i * this.track.clientWidth;
      if (Math.abs(this.track.scrollLeft - left) <= 1) return;
      this.programmatic = true;
      this.track.scrollTo({ left: left, behavior: 'auto' });
    }

    settle() {
      var wasProgrammatic = this.programmatic;
      this.programmatic = false;
      var count = this.visible.length;
      if (!count) return;
      var width = this.track.clientWidth || 1;
      var i = Math.min(count - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
      if (i === this.index) return;
      this.setActive(i, !wasProgrammatic);
    }

    stop() {
      if (!this.autoplay || this.stopped) return;
      this.stopped = true;
      this.classList.remove('is-playing');
      this.updateToggle();
    }

    resume() {
      if (!this.autoplay) return;
      this.stopped = false;
      // Reinicia a barra da imagem atual.
      this.classList.remove('is-playing');
      void this.offsetWidth;
      this.classList.toggle('is-playing', this.visible.length > 1);
      this.updateToggle();
    }

    updateToggle() {
      if (!this.toggleBtn) return;
      this.toggleBtn.dataset.state = this.stopped ? 'paused' : 'playing';
      var label = this.stopped ? this.dataset.labelPlay : this.dataset.labelPause;
      if (label) this.toggleBtn.setAttribute('aria-label', label);
    }

    setReason(name, active) {
      if (active) this.reasons.add(name);
      else this.reasons.delete(name);
      this.toggleAttribute('data-paused', this.reasons.size > 0);
    }

    syncVideos() {
      var current = this.visible ? this.visible[this.index] : null;
      var canPlay = this.inView && !this.reduce && !document.hidden;
      this.slides.forEach(function (slide) {
        var video = slide.querySelector('video');
        if (!video) return;
        if (slide === current && canPlay) {
          video.muted = true;
          var played = video.play();
          if (played && played.catch) played.catch(function () {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    // Altura ocupada pelo cabeçalho quando ele continua visível depois de rolar a página.
    // --hg-header-space guarda a altura do cabeçalho parado (não muda ao rolar) e limita a
    // altura da galeria fixa ao espaço da tela abaixo dele, com bolinhas e botões à vista.
    updateHeader() {
      var inner = document.querySelector('[data-halo-header-bar]') || document.querySelector('.xo-header__inner');
      var offset = 0;
      var space = this.headerSpace || 0;
      if (inner) {
        var rect = inner.getBoundingClientRect();
        if (window.scrollY > 0 && rect.bottom > 0 && rect.top < 120 && window.scrollY > rect.bottom) offset = Math.round(rect.bottom);
        if (rect.top >= 0 && rect.top < 120) space = Math.round(rect.bottom);
      }
      if (space !== this.headerSpace) {
        this.headerSpace = space;
        this.style.setProperty('--hg-header-space', space + 'px');
      }
      if (offset === this.headerOffset) return;
      this.headerOffset = offset;
      this.style.setProperty('--hg-header', offset + 'px');
    }

    updateAlignment() {
      if (!this.alignInfo || mobileQuery.matches) return;
      var title = this.alignInfo.querySelector('h1, h2, h3');
      var buttons = this.alignInfo.querySelectorAll('xo-cart-add button, .xo-add-to-cart--unavailable button');
      var button = Array.prototype.find.call(buttons, function (node) {
        return node.getBoundingClientRect().height > 0;
      });
      if (!title || !button) return;
      var column = this.parentElement;
      var bounds = this.alignInfo.getBoundingClientRect();
      var top = Math.max(0, title.getBoundingClientRect().top - bounds.top);
      var bottom = Math.max(0, bounds.bottom - button.getBoundingClientRect().bottom);
      column.style.setProperty('--hg-align-top', top.toFixed(2) + 'px');
      column.style.setProperty('--hg-align-bottom', bottom.toFixed(2) + 'px');
    }

    // O lado da moldura que encosta na borda da tela fica reto (só a partir de 768px, no CSS).
    updateFlush() {
      var rect = this.frame.getBoundingClientRect();
      var viewport = document.documentElement.clientWidth;
      this.classList.toggle('is-flush-left', rect.left < 1);
      this.classList.toggle('is-flush-right', viewport - rect.right < 1);
    }

    onOptionChange(e) {
      var input = e.target;
      if (!input || !input.name || !this.option) return;
      // O tema renomeia os botões para "{product_123}Cor"; o prefixo entre chaves é ignorado.
      var name = input.name.replace(/^\{[^}]*\}/, '').toLowerCase();
      var option = this.option.toLowerCase();
      if (name !== option && name !== 'options[' + option + ']') return;
      if (input.type === 'radio' && !input.checked) return;
      var color = this.values.indexOf(input.value);
      if (color < 0 || color === this.color) return;
      this.applyColor(color);
    }

    applyColor(color) {
      this.color = color;
      var token = ' ' + color + ' ';
      var matches = function (slide) {
        var cores = slide.dataset.cores || '*';
        return cores === '*' || cores.indexOf(token) !== -1;
      };
      var any = this.slides.some(matches);
      this.slides.forEach(function (slide) {
        slide.hidden = any ? !matches(slide) : false;
      });
      this.collect();
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.programmatic = false;
      this.setActive(0, false);
    }

    // Pop-up "Imagens Real-Life": faixa contínua, como o "Na Mídia", que o cliente arrasta
    // com o dedo ou o mouse. As fotos são repetidas no fim para a faixa nunca acabar.
    setupReal(on) {
      var self = this;
      this.realViewport = this.dialog.querySelector('[data-hg-real-viewport]');
      this.realTrack = this.dialog.querySelector('[data-hg-real-track]');
      this.realPrev = this.dialog.querySelector('[data-hg-real-prev]');
      this.realNext = this.dialog.querySelector('[data-hg-real-next]');
      this.realToggle = this.dialog.querySelector('[data-hg-real-toggle]');
      this.realAuto = this.dialog.dataset.autoplay === 'true' && !this.reduce;
      this.realSeconds = parseFloat(this.dialog.dataset.interval) || 4;
      this.realStopped = !this.realAuto;
      this.realOffset = 0;
      this.realFling = 0;
      this.realTween = null;
      this.realDrag = null;
      if (this.realToggle) this.realToggle.hidden = !this.realAuto;

      if (this.opener) {
        this.opener.addEventListener('click', function () {
          self.openReal();
        }, on);
      }
      this.dialog.querySelector('[data-hg-close]').addEventListener('click', function () {
        self.dialog.close();
      }, on);
      // Clique fora do painel (no fundo escurecido) fecha.
      this.dialog.addEventListener('click', function (e) {
        if (e.target === self.dialog) self.dialog.close();
      }, on);
      this.dialog.addEventListener('close', function () {
        self.syncReal();
        self.setReason('dialog', false);
        if (self.opener && self.returnFocus) self.opener.focus();
        self.returnFocus = false;
      }, on);

      if (this.realPrev) {
        this.realPrev.addEventListener('click', function () {
          self.nudgeReal(-1);
        }, on);
      }
      if (this.realNext) {
        this.realNext.addEventListener('click', function () {
          self.nudgeReal(1);
        }, on);
      }
      if (this.realToggle) {
        this.realToggle.addEventListener('click', function () {
          if (self.realStopped) self.resumeReal();
          else self.stopReal();
        }, on);
      }
      if (!this.realViewport || !this.realTrack) return;

      // Arrastar (dedo ou mouse). Enquanto o dedo ou o botão estão pressionados, a faixa espera.
      var vp = this.realViewport;
      vp.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        self.realDrag = { x: e.clientX, offset: self.realOffset, lastX: e.clientX, lastT: e.timeStamp, v: 0, moved: false, id: e.pointerId };
        self.realTween = null;
        self.realFling = 0;
      }, on);
      vp.addEventListener('pointermove', function (e) {
        var drag = self.realDrag;
        if (!drag || e.pointerId !== drag.id) return;
        var dx = e.clientX - drag.x;
        if (!drag.moved) {
          if (Math.abs(dx) < 4) return;
          drag.moved = true;
          try {
            vp.setPointerCapture(e.pointerId);
          } catch (err) {}
          vp.classList.add('is-dragging');
        }
        var dt = e.timeStamp - drag.lastT;
        if (dt >= 4) {
          drag.v = (e.clientX - drag.lastX) / dt;
          drag.lastX = e.clientX;
          drag.lastT = e.timeStamp;
        }
        self.realOffset = drag.offset - dx;
        self.renderReal();
      }, on);
      var end = function (e) {
        var drag = self.realDrag;
        if (!drag || e.pointerId !== drag.id) return;
        self.realDrag = null;
        vp.classList.remove('is-dragging');
        if (!drag.moved) return;
        // Embalo ao soltar (limitado), que vai sumindo enquanto a faixa volta ao ritmo normal.
        var velocity = e.timeStamp - drag.lastT > 100 ? 0 : Math.max(-3, Math.min(3, drag.v));
        self.realFling = -velocity;
      };
      vp.addEventListener('pointerup', end, on);
      vp.addEventListener('pointercancel', end, on);
      vp.addEventListener('dragstart', function (e) {
        e.preventDefault();
      }, on);
      vp.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        self.nudgeReal(e.key === 'ArrowLeft' ? -1 : 1);
      }, on);
    }

    openReal() {
      if (!this.dialog || this.dialog.open) return;
      this.returnFocus = document.activeElement === this.opener;
      this.dialog.showModal();
      this.setReason('dialog', true);
      this.realStopped = !this.realAuto;
      this.updateRealToggle();
      this.realOffset = 0;
      this.realFling = 0;
      this.realTween = null;
      this.measureReal();
      this.syncReal();
    }

    // Mede a faixa e repete as fotos o bastante para cobrir a tela mais uma volta inteira.
    measureReal() {
      if (!this.dialog || !this.dialog.open || !this.realTrack) return;
      var track = this.realTrack;
      Array.prototype.forEach.call(track.querySelectorAll('[data-hg-clone]'), function (node) {
        node.remove();
      });
      var photos = Array.prototype.slice.call(track.querySelectorAll('[data-hg-foto]'));
      if (!photos.length) return;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var last = photos[photos.length - 1];
      this.realLoop = last.offsetLeft + last.offsetWidth + gap - photos[0].offsetLeft;
      this.realStep = photos.length > 1 ? photos[1].offsetLeft - photos[0].offsetLeft : this.realLoop;
      this.realSpeed = this.realStep / (this.realSeconds * 1000);
      var sets = Math.max(1, Math.ceil(this.realViewport.clientWidth / this.realLoop));
      for (var i = 0; i < sets; i++) {
        photos.forEach(function (photo) {
          var clone = photo.cloneNode(true);
          clone.removeAttribute('data-hg-foto');
          Array.prototype.slice.call(clone.attributes).forEach(function (attr) {
            if (attr.name.indexOf('data-shopify') === 0) clone.removeAttribute(attr.name);
          });
          Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (node) {
            node.removeAttribute('id');
          });
          clone.setAttribute('data-hg-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          track.appendChild(clone);
        });
      }
      this.dialog.classList.add('has-overflow');
      this.renderReal();
    }

    // Liga o movimento só com o pop-up aberto e a aba visível.
    syncReal() {
      var run = !!(this.dialog && this.dialog.open && !document.hidden && this.realLoop);
      if (run && !this.realRaf) {
        this.realLast = 0;
        this.realRaf = requestAnimationFrame(this.tickReal.bind(this));
      } else if (!run && this.realRaf) {
        cancelAnimationFrame(this.realRaf);
        this.realRaf = 0;
      }
    }

    tickReal(time) {
      this.realRaf = 0;
      if (!this.dialog || !this.dialog.open) return;
      var dt = this.realLast ? Math.min(64, time - this.realLast) : 16;
      this.realLast = time;
      if (!this.realDrag) {
        if (this.realTween) {
          var tween = this.realTween;
          var p = Math.min(1, (time - tween.start) / tween.duration);
          this.realOffset = tween.from + (tween.to - tween.from) * (1 - Math.pow(1 - p, 3));
          if (p >= 1) this.realTween = null;
        } else {
          if (!this.realStopped) this.realOffset += this.realSpeed * dt;
          if (this.realFling) {
            this.realOffset += this.realFling * dt;
            this.realFling *= Math.pow(0.94, dt / 16);
            if (Math.abs(this.realFling) < 0.005) this.realFling = 0;
          }
        }
      }
      this.renderReal();
      this.realRaf = requestAnimationFrame(this.tickReal.bind(this));
    }

    renderReal() {
      if (!this.realTrack || !this.realLoop) return;
      var loop = this.realLoop;
      var pos = ((this.realOffset % loop) + loop) % loop;
      this.realTrack.style.transform = 'translate3d(' + (-pos).toFixed(2) + 'px,0,0)';
      var visible = Math.max(0.15, Math.min(1, this.realViewport.clientWidth / loop));
      this.dialog.style.setProperty('--hg-thumb-w', (visible * 100).toFixed(2) + '%');
      this.dialog.style.setProperty('--hg-thumb-x', (((pos / loop) * (1 - visible)) / visible * 100).toFixed(2) + '%');
    }

    // Setas e teclado: uma foto para cada lado, com animação curta.
    nudgeReal(direction) {
      if (!this.realStep) return;
      var to = this.realOffset + direction * this.realStep;
      this.realFling = 0;
      if (this.reduce) {
        this.realTween = null;
        this.realOffset = to;
        this.renderReal();
        return;
      }
      this.realTween = { from: this.realOffset, to: to, start: performance.now(), duration: 450 };
    }

    stopReal() {
      if (!this.realAuto || this.realStopped) return;
      this.realStopped = true;
      this.updateRealToggle();
    }

    resumeReal() {
      if (!this.realAuto) return;
      this.realStopped = false;
      this.updateRealToggle();
    }

    updateRealToggle() {
      if (!this.realToggle) return;
      this.realToggle.dataset.state = this.realStopped ? 'paused' : 'playing';
      var label = this.realStopped ? this.dataset.labelPlay : this.dataset.labelPause;
      if (label) this.realToggle.setAttribute('aria-label', label);
    }

    // Editor de temas: a foto selecionada vai para o começo da faixa.
    scrollToPhoto(photo) {
      if (!this.realTrack) return;
      var first = this.realTrack.querySelector('[data-hg-foto]');
      if (!first) return;
      this.realTween = null;
      this.realFling = 0;
      this.realOffset = photo.offsetLeft - first.offsetLeft;
      this.renderReal();
    }
  }

  customElements.define('halo-galeria', HaloGaleria);
})();
